import React, { useMemo } from 'react';
import { styles } from './CardGrid.css';
import { GridSlot } from './GridSlot';
import { assignGridSlots } from './helpers';
import { ContentCard, ContentCardProps } from '../ContentCard';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { HeroTile } from '../../HeroTile/HeroTile';
import { PossiblyResponsiveValue, useResponsiveValue } from '../../hooks/useResponsiveValue';

export enum CardGridVariant {
  default = 'default',
  withPrimary = 'withPrimary',
}

export type CardGridCardProps = ContentCardProps & { isFixedHeight?: boolean };

export type CardGridProps = {
  cards: Array<CardGridCardProps>;
  variant?: CardGridVariant;
  isCarousel?: PossiblyResponsiveValue<boolean>;
};

export const CardGrid = ({ cards, variant = CardGridVariant.default, isCarousel: isCarouselProp }: CardGridProps) => {
  const isDefault = variant === CardGridVariant.default;
  const isCarousel = useResponsiveValue(
    isCarouselProp ?? (isDefault ? { xs: true, lg: false } : { xs: true, md: false })
  );
  const gridSlotData = useMemo(() => assignGridSlots(cards), [cards]);

  if (!cards.length) {
    return null;
  }
  if (isCarousel) {
    return <CardGridCarousel cards={cards} />;
  }

  return (
    <div className={styles.cardGrid({ variant })}>
      {cards.map((cardProps, index) => {
        if (isDefault) {
          return <CardGridCard {...cardProps} key={index} />;
        }
        const { isPrimary, slots } = gridSlotData[index];
        return (
          <GridSlot key={index} isPrimary={isPrimary} slots={slots}>
            <CardGridCard
              {...cardProps}
              {...(isPrimary ? { headingSize: 'large' } : !cardProps.isFixedHeight ? { content: null } : {})}
              key={index}
            />
          </GridSlot>
        );
      })}
    </div>
  );
};

const CardGridCarousel = ({ cards }: { cards: Array<CardGridCardProps> }) => {
  const [fixedCards, standardCards] = useMemo(
    () =>
      cards.reduce(
        ([fixed, standard]: Array<Array<CardGridCardProps>>, article) => {
          (article.isFixedHeight ? fixed : standard).push(article);
          return [fixed, standard];
        },
        [[], []]
      ),
    [cards]
  );

  return (
    <div className={styles.carouselWrapper}>
      <Carousel
        showProgressBar
        spaceBetween={10}
        slidesOffsetAfter={2}
        fullBleed={true}
        showPagination={false}
        showArrows={false}
        slidesPerView="auto"
      >
        {standardCards.map((cardProps, index) => (
          <CardGridCard key={`standard-${index}`} {...cardProps} width="345px" />
        ))}
      </Carousel>
      {fixedCards.map((cardProps, index) => (
        <CardGridCard key={`fixed-${index}`} {...cardProps} />
      ))}
    </div>
  );
};

const CardGridCard = ({ isFixedHeight = false, ...cardProps }: CardGridCardProps) => {
  if (isFixedHeight) {
    return <HeroTile {...cardProps} />;
  }
  return <ContentCard {...cardProps} />;
};
